import { useState, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import { getVehicleImageUrl } from "@utils/vehicleImage";

interface VehicleImageProps {
  marque?: string;
  modele?: string;
  coloris?: string;
  mec?: number;
  alt?: string;
  className?: string;
  iconSize?: number;
}

export default function VehicleImage({
  marque,
  modele,
  coloris,
  mec,
  alt,
  className = "w-full h-full",
  iconSize = 40,
}: VehicleImageProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const url = getVehicleImageUrl(marque, modele, coloris, mec);
  const prevUrlRef = useRef(url);

  // Reset loading/error synchronously during render when URL changes
  // (avoids race condition where onLoad fires before useEffect)
  if (prevUrlRef.current !== url) {
    prevUrlRef.current = url;
    setLoading(true);
    setError(false);
  }

  if (!url || error) {
    return <Icon1 name="image" size={iconSize} color="#D4D4D4" />;
  }

  return (
    <div className={`${className} relative overflow-hidden`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-50 rounded-lg">
          {/* Shimmer sweep */}
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 60%, transparent 100%)",
              backgroundSize: "200% 100%",
              animation: "vehicleShimmer 1.4s ease-in-out infinite",
            }}
          />
          {/* Car silhouette */}
          <Icon1 name="car" size={Math.max(iconSize, 28)} color="#E0E0E0" />
        </div>
      )}
      <img
        src={url}
        alt={alt || `${marque} ${modele}`}
        className={`w-full h-full object-contain transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
        onLoad={() => setLoading(false)}
        onError={() => { setLoading(false); setError(true); }}
      />
      <style>{`
        @keyframes vehicleShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
